import { AwsRum, AwsRumConfig } from 'aws-rum-web'
import Auth from '@aws-amplify/auth'
import config from '@/config'

export enum CompsOperation {
  COMPS_RENDERING_TIME = 'COMPS_RENDERING_TIME',
  COMPS_TOTAL_TIME_TO_READY = 'COMPS_TOTAL_TIME_TO_READY',
  COMPS_ABANDONED = 'COMPS_ABANDONED',
  COMPS_PRE_WORKBOOK_SETUP = 'COMPS_PRE_WORKBOOK_SETUP',
  COMPS_WORKBOOK_SETUP = 'COMPS_WORKBOOK_SETUP',
}

const endpoint = `https://monitoring.${config.STAGE}.cognitivecredit.com`

const getStackTrace = (): string[] => {
  let stack: string

  try {
    throw new Error('')
  } catch (error) {
    stack = (error as Error).stack || ''
  }

  const lines = stack.split('\n').map((line) => line.trim())
  return lines.splice(lines[0] === 'Error' ? 2 : 1)
}

const authConfig: AwsRumConfig = {
  sessionEventLimit: 0,
  sessionSampleRate: 1,
  endpoint,
  telemetries: [
    [
      'http',
      {
        recordAllRequests: true,
        urlsToExclude: [/\*.js/, /\*.svg/],
      },
    ],
    'errors',
  ],
  allowCookies: true,
  signing: false,
  enableXRay: false,
  cookieAttributes: { unique: true },
}

const unauthConfig: AwsRumConfig = {
  sessionEventLimit: 100,
  sessionSampleRate: 1,
  guestRoleArn: config.AWS_RUM_GUEST_ROLE,
  identityPoolId: config.AWS_RUM_UNAUTH_IDENTITY_POOL_ID,
  endpoint,
  telemetries: [
    [
      'http',
      {
        recordAllRequests: true,
        urlsToExclude: [/\*.js/, /\*.svg/],
      },
    ],
    'errors',
  ],
  allowCookies: false,
  signing: false,
  enableXRay: false,
}

let awsRum: AwsRum | null = null

const AUTH_APPLICATION_ID = config.AWS_RUM_AUTH_MONITOR_ID
const UNAUTH_APPLICATION_ID = config.AWS_RUM_UNAUTH_MONITOR_ID
const APPLICATION_VERSION = '1.0.0'
const APPLICATION_REGION = config.AWS_REGION
const originalConsoleWarn = console.warn
const originalConsoleError = console.error

const initRum = async (): Promise<void> => {
  // no need to enable these for local development, especially considering
  // we'll be getting rejected 404s because the domains don't match
  if (!['127.0.0.1', 'localhost'].includes(window.location.hostname)) {
    try {
      await Auth.currentAuthenticatedUser()
      const currentCredentials = await Auth.currentCredentials()
      awsRum = new AwsRum(AUTH_APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, authConfig)

      awsRum.setAwsCredentials(currentCredentials)
    } catch (e) {
      if (e === 'The user is not authenticated') {
        awsRum = new AwsRum(UNAUTH_APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, unauthConfig)
      }
    } finally {
      console.warn = function (...args: unknown[]) {
        awsRum?.recordEvent('com.cc.console.warning', {
          message: JSON.stringify(args),
          stack: getStackTrace(),
        })
        originalConsoleWarn.apply(console, args)
      }
      console.error = function (...args: unknown[]) {
        // TODO: Do we want to integrate this with their own
        // registerError method? We are capturing errors...
        awsRum?.recordEvent('com.cc.console.error', {
          message: JSON.stringify(args),
          stack: getStackTrace(),
        })
        originalConsoleError.apply(console, args)
      }
    }
  }
}

const switchRum = async (): Promise<void> => {
  if (awsRum) {
    awsRum.dispatch()
    awsRum.disable()
  }
  await initRum()
}

const recordCompsEvents = (route: string, operation: CompsOperation, duration: number): void => {
  const locale = navigator.language || 'en-US'
  const countryCode = locale.split('-')[1]?.toUpperCase() || 'UNKNOWN'

  awsRum?.recordEvent(`com.cc.${operation}`, {
    pageId: route,
    operation: operation,
    duration: duration,
    //Not 100% reliable but should be good enough.
    countryCode: countryCode,
  })
}

const recordCustomEvent = (eventType: string, eventData: Record<string, unknown>): void => {
  awsRum?.recordEvent(eventType, eventData)
}

const getRumInstance = (): AwsRum | null => awsRum

export const useRum = () => {
  return {
    initRum,
    switchRum,
    recordCompsEvents,
    recordCustomEvent,
    getRumInstance,
  }
}
